import React, { useState } from 'react';
import { Table, TableHead, TableBody, TableRow, MenuItem } from '@mui/material';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useToast } from 'use-toast-mui';
import QueryString from 'qs';
import moment from 'moment';
import KeyboardArrowLeftIcon from '@mui/icons-material/KeyboardArrowLeft';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';

import CommonDatePicker from '../../../form-components/CommonDatePicker';
import SmsChargeModal from '../../member/modal/sms/SmsChargeModal';
import { getSmsBalance, getSmsBranchOrderList, cancelSmsOrder } from '../../../api/sms';
import {
  ControlsRow,
  PageSizeGroup,
  PageSizeLabel,
  PageSizeSelect,
  SearchGroup,
  DateRangeGroup,
  PresetSelect,
  DateFieldWrapper,
  DateDivider,
  SearchInput,
  SearchButton,
  ChargeButton,
  TableWrapper,
  HeaderCell,
  BodyCell,
  DataRow,
  StatusText,
  CancelButton,
  PaginationContainer,
  PageButton,
  NavButton,
  EllipsisText,
} from './SmsChargeHistoryStyled';

const COLUMNS = [
  { id: 'branchName', label: '지점명' },
  { id: 'createdAt', label: '충전일시' },
  { id: 'confirmedAt', label: '승인일시' },
  { id: 'tokenCount', label: '충전건수' },
  { id: 'totalPrice', label: '결제금액' },
  { id: 'depositorName', label: '입금자 명' },
  { id: 'remainingCount', label: '잔여건수' },
  { id: 'orderStatus', label: '상태' },
  { id: 'action', label: '취소' },
];

const STATUS_MAP = {
  PENDING_PAYMENT: '승인대기',
  PAID: '충전완료',
  CHARGED: '충전완료',
  CANCELLED: '취소',
  CANCELED: '취소',
};

const COMPLETED_STATUSES = ['PAID', 'CHARGED'];

const PRESET_OPTIONS = [
  { value: 'today', label: '오늘' },
  { value: '7days', label: '최근 7일' },
  { value: '30days', label: '최근 30일' },
  { value: 'custom', label: '직접선택' },
];

const getDateRangeByPreset = preset => {
  const today = moment();
  switch (preset) {
    case '7days':
      return { from: today.clone().subtract(6, 'days').toDate(), to: today.toDate() };
    case '30days':
      return { from: today.clone().subtract(29, 'days').toDate(), to: today.toDate() };
    case 'today':
    default:
      return { from: today.toDate(), to: today.toDate() };
  }
};

const SmsChargeHistory = () => {
  const { t } = useTranslation();
  const { user } = useSelector(state => state.user);
  const toast = useToast();
  const queryClient = useQueryClient();
  const branchId = user?.branch?.branchId;

  const initialRange = getDateRangeByPreset('today');

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [preset, setPreset] = useState('today');
  const [fromDate, setFromDate] = useState(initialRange.from);
  const [toDate, setToDate] = useState(initialRange.to);
  const [searchValue, setSearchValue] = useState('');
  const [searchKeyword, setSearchKeyword] = useState('');
  const [appliedRange, setAppliedRange] = useState(initialRange);
  const [isChargeModalOpen, setIsChargeModalOpen] = useState(false);

  const query = QueryString.stringify({
    page: page - 1,
    size: pageSize,
    ...(appliedRange.from && { startDate: moment(appliedRange.from).format('YYYY-MM-DD') }),
    ...(appliedRange.to && { endDate: moment(appliedRange.to).format('YYYY-MM-DD') }),
  });

  const { data: orderData } = useQuery({
    queryKey: ['smsChargeHistory', page, pageSize, appliedRange.from, appliedRange.to],
    queryFn: () => getSmsBranchOrderList(query, user?.token, branchId),
    enabled: !!user?.token,
  });

  console.log('orderData : ' + JSON.stringify(orderData, null, 2));

  const { data: balanceData } = useQuery({
    queryKey: ['smsBalance', branchId],
    queryFn: () => getSmsBalance(branchId, user?.token),
    enabled: !!branchId && !!user?.token,
  });

  const cancelMutation = useMutation({
    mutationFn: orderId => cancelSmsOrder(branchId, orderId, user?.token),
    onSuccess: () => {
      toast.success(t('주문이 취소되었습니다'));
      queryClient.invalidateQueries({ queryKey: ['smsChargeHistory'] });
      queryClient.invalidateQueries({ queryKey: ['smsBalance', branchId] });
    },
  });

  const handleCancel = order => {
    if (window.confirm(t('해당 충전 주문을 취소하시겠습니까?'))) {
      cancelMutation.mutate(order.id);
    }
  };

  const rawOrders = orderData?.content || [];
  const keyword = searchKeyword.trim().toLowerCase();
  const rangeStart = appliedRange.from ? moment(appliedRange.from).startOf('day') : null;
  const rangeEnd = appliedRange.to ? moment(appliedRange.to).endOf('day') : null;
  const orders = rawOrders.filter(order => {
    if (rangeStart || rangeEnd) {
      const registered = order.registeredDate ? moment(order.registeredDate) : null;
      if (!registered || !registered.isValid()) return false;
      if (rangeStart && registered.isBefore(rangeStart)) return false;
      if (rangeEnd && registered.isAfter(rangeEnd)) return false;
    }
    if (keyword) {
      const branchName = (order.branchName || user?.branch?.branchName || '').toLowerCase();
      const depositorName = (order.depositorName || '').toLowerCase();
      if (!branchName.includes(keyword) && !depositorName.includes(keyword)) return false;
    }
    return true;
  });
  const totalPages = orderData?.totalPages || 1;
  const remainingBalance = balanceData?.balance ?? balanceData?.remainingCount ?? 0;

  const handlePresetChange = value => {
    setPreset(value);
    if (value !== 'custom') {
      const range = getDateRangeByPreset(value);
      setFromDate(range.from);
      setToDate(range.to);
      setAppliedRange(range);
      setPage(1);
    }
  };

  const handleSearch = () => {
    setSearchKeyword(searchValue);
    setAppliedRange({ from: fromDate, to: toDate });
    setPage(1);
  };

  const handleKeyDown = e => {
    if (e.key === 'Enter') handleSearch();
  };

  const formatDateTime = value => {
    if (!value) return '-';
    const m = moment(value);
    return m.isValid() ? m.format('YYYY-MM-DD HH:mm') : '-';
  };

  const renderCellValue = order => columnId => {
    switch (columnId) {
      case 'branchName':
        return order.branchName || user?.branch?.branchName || '-';
      case 'createdAt':
        return formatDateTime(order.registeredDate);
      case 'confirmedAt':
        return COMPLETED_STATUSES.includes(order.orderStatus)
          ? formatDateTime(order.confirmedAt || order.approvedAt)
          : '-';
      case 'tokenCount':
        return order.tokenCount != null ? `${order.tokenCount.toLocaleString()}건` : '-';
      case 'totalPrice': {
        const price = order.totalPrice ?? order.amount ?? order.paymentAmount;
        return price != null ? `${Number(price).toLocaleString()}원` : '-';
      }
      case 'depositorName':
        return order.depositorName || '-';
      case 'remainingCount': {
        if (!COMPLETED_STATUSES.includes(order.orderStatus)) return '-';
        const remaining = order.remainingCount ?? order.balanceAfter;
        return remaining != null ? `${Number(remaining).toLocaleString()}건` : '-';
      }
      case 'orderStatus':
        return (
          <StatusText status={order.orderStatus}>
            {t(STATUS_MAP[order.orderStatus] || order.orderStatus || '-')}
          </StatusText>
        );
      case 'action':
        return order.orderStatus === 'PENDING_PAYMENT' ? (
          <CancelButton onClick={() => handleCancel(order)} disabled={cancelMutation.isPending}>
            {t('취소')}
          </CancelButton>
        ) : (
          '-'
        );
      default:
        return '-';
    }
  };

  const renderPagination = () => {
    const maxVisible = 3;
    let startPage = Math.max(1, page - 1);
    let endPage = Math.min(totalPages, startPage + maxVisible - 1);
    if (endPage - startPage < maxVisible - 1) {
      startPage = Math.max(1, endPage - maxVisible + 1);
    }

    const pages = [];
    for (let i = startPage; i <= endPage; i++) pages.push(i);

    const showLastEllipsis = endPage < totalPages - 1;
    const showLastPage = endPage < totalPages;

    return (
      <PaginationContainer>
        <NavButton onClick={() => setPage(prev => Math.max(1, prev - 1))} disabled={page === 1}>
          <KeyboardArrowLeftIcon />
        </NavButton>
        {pages.map(p => (
          <PageButton key={p} isActive={p === page} onClick={() => setPage(p)}>
            {p}
          </PageButton>
        ))}
        {showLastEllipsis && <EllipsisText>...</EllipsisText>}
        {showLastPage && (
          <PageButton isActive={page === totalPages} onClick={() => setPage(totalPages)}>
            {totalPages}
          </PageButton>
        )}
        <NavButton onClick={() => setPage(prev => Math.min(totalPages, prev + 1))} disabled={page === totalPages}>
          <KeyboardArrowRightIcon />
        </NavButton>
      </PaginationContainer>
    );
  };

  return (
    <>
      <ControlsRow>
        <PageSizeGroup>
          <PageSizeLabel>{t('페이지당 항목 수')}</PageSizeLabel>
          <PageSizeSelect
            value={pageSize}
            onChange={e => {
              setPageSize(e.target.value);
              setPage(1);
            }}>
            <MenuItem value={10}>10</MenuItem>
            <MenuItem value={20}>20</MenuItem>
            <MenuItem value={50}>50</MenuItem>
          </PageSizeSelect>
        </PageSizeGroup>

        <SearchGroup>
          <DateRangeGroup>
            <PresetSelect value={preset} onChange={e => handlePresetChange(e.target.value)}>
              {PRESET_OPTIONS.map(opt => (
                <MenuItem key={opt.value} value={opt.value}>
                  {t(opt.label)}
                </MenuItem>
              ))}
            </PresetSelect>
            <DateFieldWrapper>
              <CommonDatePicker
                placeholder="시작일"
                value={fromDate}
                onChange={date => {
                  setFromDate(date);
                  setPreset('custom');
                  setAppliedRange(prev => ({ ...prev, from: date }));
                  setPage(1);
                }}
              />
            </DateFieldWrapper>
            <DateDivider>~</DateDivider>
            <DateFieldWrapper>
              <CommonDatePicker
                placeholder="종료일"
                value={toDate}
                onChange={date => {
                  setToDate(date);
                  setPreset('custom');
                  setAppliedRange(prev => ({ ...prev, to: date }));
                  setPage(1);
                }}
                minDate={fromDate}
              />
            </DateFieldWrapper>
          </DateRangeGroup>

          <SearchInput
            placeholder={t('검색어를 입력해 주세요')}
            value={searchValue}
            onChange={e => setSearchValue(e.target.value)}
            onKeyDown={handleKeyDown}
          />
          <SearchButton onClick={handleSearch}>{t('검색')}</SearchButton>
        </SearchGroup>

        <ChargeButton onClick={() => setIsChargeModalOpen(true)}>{t('SMS 충전하기')}</ChargeButton>
      </ControlsRow>

      <TableWrapper>
        <Table>
          <TableHead>
            <TableRow>
              {COLUMNS.map(col => (
                <HeaderCell key={col.id}>{t(col.label)}</HeaderCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {orders.length > 0 ? (
              orders.map((order, idx) => {
                const getValue = renderCellValue(order);
                return (
                  <DataRow key={order.id || idx}>
                    {COLUMNS.map(col => (
                      <BodyCell key={col.id}>{getValue(col.id)}</BodyCell>
                    ))}
                  </DataRow>
                );
              })
            ) : (
              <DataRow>
                <BodyCell colSpan={COLUMNS.length} style={{ textAlign: 'center', color: '#a6a6a6' }}>
                  {t('데이터가 없습니다')}
                </BodyCell>
              </DataRow>
            )}
          </TableBody>
        </Table>
      </TableWrapper>

      {renderPagination()}

      {isChargeModalOpen && (
        <SmsChargeModal
          open={isChargeModalOpen}
          onClose={() => setIsChargeModalOpen(false)}
          remainingCount={remainingBalance}
          balance={remainingBalance}
        />
      )}
    </>
  );
};

export default SmsChargeHistory;
